/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { TFunction } from 'i18next';
import { useLang } from 'hooks/lang';

import { actions } from 'entities';

import {
  Switch,
  Button,
  Collapse,
  Column,
  Row,
  Label,
  LabeledInput,
  IconButton,
} from 'components/Blocks';
import { BookMinusIcon, FolderUpIcon, HashtagIcon, IconProps, XIcon } from 'components/Icons';

import CostFormula from './CostFormula';
import CostVariables from './CostVariables';
import ServiceTags from './ServiceTags';

import { useToast } from 'providers/Toast';
import { useWatch } from 'hooks';
import { useTeamProfile } from '../../context';

import { validateService } from './validate';
import { omitUndefined } from 'utils/object';

import { FormTeamService } from './types';
import { ServiceFileNames } from './FileNames';

interface ServiceFormProps {
  children?: never;
  close: () => void;
  service: FormTeamService | null;
}

export const booleanRenders: {
  property: keyof FormTeamService;
  getLabel: (t: TFunction) => string;
  disabled?: boolean;
  Icon: (p: IconProps) => JSX.Element;
}[] = [
  {
    property: 'requireSampleProjectIds',
    getLabel: (t: TFunction) => t('require-related-sample-projects-info'),
    disabled: true,
    Icon: HashtagIcon,
  },
  {
    property: 'requireExcludedOWIds',
    getLabel: (t: TFunction) => t('require-acknowledgement-of-excluded-OW-ids'),
    Icon: BookMinusIcon,
  },
  {
    property: 'canCreateSampleProject',
    getLabel: (t: TFunction) => t('can-create-sample-project'),
    Icon: FolderUpIcon,
  },
];

function ServiceForm({ close, service }: ServiceFormProps) {
  const [data, setData] = useState(service);
  useWatch(service, setData);

  const { team, updateTeam } = useTeamProfile();

  const { t } = useLang();
  const { addToast } = useToast();

  const createService = actions.teams.createService.use({
    onSuccess: (newService) => {
      updateTeam((old) => ({
        ...old,
        services: [...old.services, newService as any],
      }));

      close();
    },
  });

  const updateService = actions.teams.updateService.use({
    onSuccess: (mutatedSvs) => {
      updateTeam((old) => ({
        ...old,
        services: old.services.map((svs) => (svs.id === mutatedSvs.id ? (mutatedSvs as any) : svs)),
      }));

      close();
    },
  });

  const loading = createService.loading || updateService.loading;

  const handleSaveClick = async () => {
    const {
      name,
      id,
      tags,
      costVariables,
      costCalculation,
      requireExcludedOWIds,
      canCreateSampleProject,
      fileNames = [],
    } = data as FormTeamService;

    const serviceToValidate = {
      ...(data as FormTeamService),
      services: team.services,
    };

    const fillingError = validateService(serviceToValidate, t);

    if (fillingError) return addToast(fillingError);

    const isUpdate = !!id;

    const mutation = isUpdate ? updateService : createService;

    mutation.execute(
      omitUndefined({
        name,
        tags,
        costVariables,
        costCalculation,
        id,
        team: team.id,
        requireExcludedOWIds,
        canCreateSampleProject,
        fileNames,
      }),
    );
  };

  return (
    <Collapse expanded={!!service} onlyRenderIfExpanded>
      <Column className="rounded-md border p-5 shadow-md" gap="8">
        <Row className="mb-2" justifyContent="space-between">
          <h6>{service?.createdAt ? t('edit-service') : t('create-service')}</h6>

          <IconButton onClick={close}>
            <XIcon size={14} />
          </IconButton>
        </Row>

        <LabeledInput
          required
          name="name"
          label={t('name')}
          value={data?.name || ''}
          onChange={({ target }) =>
            setData((old) => ({ ...old, name: target.value }) as FormTeamService)
          }
        />

        <ServiceTags service={data} setService={setData} />

        <CostVariables service={data} setService={setData} />

        <ServiceFileNames service={data} setService={setData} />

        {!!data && <CostFormula service={data} setService={setData} />}

        <Column>
          <p>{t('extensions')}</p>

          <Row className="phone:flex-col phone:items-start">
            {booleanRenders.map(({ property, getLabel, disabled }) => (
              <Row key={property} gap="2">
                <Switch
                  id={property}
                  checked={!!data?.[property]}
                  disabled={disabled}
                  onChange={({ target }) =>
                    setData((old) => ({ ...old, [property]: target.checked }) as FormTeamService)
                  }
                />

                <Label className="font-normal" htmlFor={property}>
                  {getLabel(t)}
                </Label>
              </Row>
            ))}
          </Row>
        </Column>

        <Row justifyContent="flex-end">
          <Button variant="outlined" onClick={close}>
            {t('close')}
          </Button>

          <Button onClick={handleSaveClick} loading={loading}>
            {t('save')}
          </Button>
        </Row>
      </Column>
    </Collapse>
  );
}

export type { FormTeamService };

export default ServiceForm;
